import { axios } from "../utils/index"
import helpers from "utils/helpers";
import { generateFcmToken } from "../utils/getFcmToken";

const userServices = {
  async login(loginData) {
    const fcmToken = await generateFcmToken();
    const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/login`,  
    {
      ...loginData,
      fcmToken
    });
    return res;
  },

  async forgotPasswordEmail(email, adminId) {
    try {
      const payload = adminId ? { email, adminId } : { email };

      const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/forgot-password`, payload);
      return res.data;
    } catch (error) {
      return { error: error.response?.data?.error || "Request failed" };
    }
  },

  async resetPassword(data) {
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/reset-password`, data);
      return res.data;
    } catch (error) {
      return { error: error.response?.data?.error || "Reset failed" };
    }
  },
  async updatePassword(data) {
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/update-password`, data);
      return res.data;
    } catch (error) {
      return { error: error.response?.data?.error || "Reset failed" };
    }
  },

  async getUserDetails(userId) {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/user/${userId}`
    );
    return res;
  },

  async getUserFlaggedHistory(userId, params = {}) {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/user/${userId}/flagged-history`,
      { params }
    );
    return res.data;
  },

  async userVerification(userId, payload) {
    const res = await axios.patch(
      `${process.env.REACT_APP_API_URL}/user/${userId}/verification-status`,
      payload
    );
    return res;
  },

  async userProfileVerification(userId, payload) {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/${userId}/profile-picture`,
      payload
    );
    return res;
  },

  async getAllUsers(payload = {}) {
    return axios.get(
      `${process.env.REACT_APP_API_URL}/user`,
      { params: payload }
    );
  },

  async deleteUser(payload) {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/user/`, 
        {
          data: payload
        }
      );
      return await response.data;
    } catch (error) {
      console.error("Error deleting support requests:", error);
      return [];
    }
  },
    
  async updateUserDetails(userId, payload) {
    const res = await axios.patch(
      `${process.env.REACT_APP_API_URL}/user/${userId}`,
      payload
    );
    return res;
  },

  async reinstateUser(userId) {
    const res = await axios.patch(
      `${process.env.REACT_APP_API_URL}/user/${userId}/reinstate`
    );
    return res;
  },

  async getAllListings(payload) {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/sender-listing/fetch-admin-listings`,
      { params: payload }
    );
    return res;
  },


  async getListingDetails(listingId, type) {
    let apiUrl = '';
    if (type.toLowerCase() === helpers.CONVERSATION_ROLES.SENDER) {
      apiUrl = `${process.env.REACT_APP_API_URL}/sender-listing/${listingId}`
    } else {
      apiUrl = `${process.env.REACT_APP_API_URL}/traveler-listing/${listingId}`
    }
    const res = await axios.get(apiUrl);
    return res;
  },

  async getListingHistory(listingId, type, { page = 1, limit = 10 } = {}) {
    if (!type) {
      console.error("getListingHistory: `type` is required but was", type);
      return null;
    }
    const isSender = type.toLowerCase() === helpers.CONVERSATION_ROLES.SENDER;
    const base = isSender
      ? `${process.env.REACT_APP_API_URL}/sender-listing/history/${listingId}`
      : `${process.env.REACT_APP_API_URL}/traveler-listing/history/${listingId}`;
    const res = await axios.get(base, { params: { type: isSender ? "sender" : "traveler", page, limit } });
    return res;
  },
  
  async updateListings(listingId, payload) {
    const res = await axios.patch(
      `${process.env.REACT_APP_API_URL}/sender-listing/update-listings/${listingId}`,
      payload
    );
    return res;
  },

  async adminResolveCancellation(payload) {
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      throw new Error("Invalid payload: payload must be a valid object.");
    }

    const isSender =
      String(payload.listingType || payload.type || "").trim().toLowerCase() === "sender";

    const sanitizedPayload = {
      listingId: String(payload.listingId || "").trim(),
      resolution: String(payload.resolution || "").trim().toLowerCase(),
      adminNotes: typeof payload.adminNotes === "string" ? payload.adminNotes.trim() : "",
      listingType: isSender ? "Sender" : "Traveler",
    };

    if (!sanitizedPayload.listingId) {
      throw new Error("Validation Error: listingId is required.");
    }

    if (!["approve", "approved", "reject", "rejected"].includes(sanitizedPayload.resolution)) {
      throw new Error("Validation Error: resolution must be 'approve' or 'reject'.");
    }

    const endpoint = isSender
      ? `${process.env.REACT_APP_API_URL}/sender-listing/cancellation-request/admin-resolve`
      : `${process.env.REACT_APP_API_URL}/traveler-listing/cancellation-request/admin-resolve`;

    try {
      const res = await axios.post(endpoint, sanitizedPayload);
      return res;
    } catch (error) {
      console.error("Admin resolution failed:", error);
      throw error;
    }
  },

  async deleteUsersListing(payload, listingType) {
    let apiUrl;
  
    if (listingType === 'Traveler') {
      apiUrl = `${process.env.REACT_APP_API_URL}/traveler-listing/delete-traveler-listing`;
    } else if (listingType === 'Sender') {
      apiUrl = `${process.env.REACT_APP_API_URL}/sender-listing/delete-sender-listing`;
    } else {
      throw new Error("Invalid listing type");
    }
  
    const res = await axios.post(apiUrl, payload);
    return res;
  },
  
  async fetchOrders(params = {}) {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/orders`,{ params });
      return response.data;
    } catch (error) {
      console.error("Error fetching orders:", error);
      return { data: [], pagination: {} };
    }
  },

  async deleteOrder(orderId) {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting orders:", error);
      throw error;
    }
  },

  async updateOrderStatus(orderId, status) {
    const res = await axios.patch(
      `${process.env.REACT_APP_API_URL}/orders/${orderId}`,
      { status }
    );
    return res;
  },

  async confirmPickup(orderId, payload) {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/orders/${orderId}/confirm-pickup`,
      payload
    );
    return res.data;
  },

  async fetchOrderDetails(orderId) {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/orders/${orderId}`);
      return await response.data;
    } catch (error) {
      console.error("Error fetching support requests:", error);
      return [];
    }
  },

  async adminOverview() {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/orders/count/admin`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching order overview:", error);
      throw error;
    }
  },

  async fetchSupportRequests(params = {}) {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/support-requests`,
      {
        params: {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          search: params.search,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching support requests:", error);
    return {
      requests: [],
      total: 0,
      totalPages: 0,
      page: params.page,
      limit: params.limit,
    };
  }
},

  async fetchModerators() {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/support-requests/moderators`);
      return await response.data;
    } catch (error) {
      console.error("Error fetching support requests:", error);
      return [];
    }
  },

  async updateSupportRequest(id, payload) {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/support-requests/${id}`, payload,
         {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating support request:", error);
      return { success: false, error };
    }
  },
   

  async deleteSupportRequest(payload) {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/support-requests/delete-request`, payload);
      return await response.data;
    } catch (error) {
      console.error("Error deleting support requests:", error);
      return [];
    }
  },

  async getPermission() {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/role/permission`);
      return await response.data;
    } catch (error) {
      console.error("Error deleting support requests:", error);
    }
  },

  async fetchOrderConversation(payload) {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/messages/get-messages`,
        {
          params: payload,
        }
      );
      return await response.data;
    } catch (error) {
      console.error("Error fetching conversation messages:", error);
      return [];
    }
  },

  async createConversationMessage(payload) {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/messages/send`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (error) {
      console.error("Error sending conversation message:", error);
      throw error;
    }
  },

  async updateConversationMessage(payload) {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/messages/update-message`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error updating conversation message:", error);
      return { success: false, error };
    }
  },

  async fetchSupportRequestDetails(ticketId) {
  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}/support-requests/request/${ticketId}`
  );
  return response.data;
},

  async fetchDisputeDetails(disputeId) {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/orders/disputes/${disputeId}`,
    );
    return res.data;
  },

  async updateDisputeRequest(id, payload, isFormData = true) {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/orders/disputes/${id}`,
        payload,
        isFormData ? {
          headers: { "Content-Type": "multipart/form-data" },
        } : {}
      );
      return response.data;
    } catch (error) {
      console.error("Error updating dispute:", error);
      return { success: false, error };
    }
  },

  async getPopularRoutes() {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/dashboard/popular-routes`);
      return res;
    } catch (error) {
      return { error: error.response?.data?.message || "Request failed" };
    }
  },

  async getDeliveryPricing(filter = "7d") {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/dashboard/delivery-pricing?filter=${filter}`);
      return res;
    } catch (error) {
      return { error: error.response?.data?.message || "Request failed" };
    }
  },

  async getUserSignups(filter = "7d") {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/dashboard/users-signups?filter=${filter}`);
      return res;
    } catch (error) {
      return { error: error.response?.data?.message || "Request failed" };
    }
  },
  
  async getTotalUsersRegistration(filter = "7d") {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/dashboard/users-total?filter=${filter}`);
      return res;
    } catch (error) {
      return { error: error.response?.data?.message || "Request failed" };
    }
  },
  
  async getUserStats() {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/dashboard/delivery-statistics`);
      return res;
    } catch (error) {
      return { error: error.response?.data?.message || "Request failed" };
    }
  },

  async getAllRoles() {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/role`);
      return res.data;
    } catch (error) {
      console.error("fetchRoles error:", error.response);
      return { error: error.response?.data?.error || "Request failed" };
    }
  },

  async handleListingEmailAction(token) {
    return axios.get(
      `${process.env.REACT_APP_API_URL}/user/listings/admin-confirmation`,
      {
        params: { token },
      }
    );
  },

  // Translations
  async getTranslations(languageCode = "en") {
    return axios.get(
      `${process.env.REACT_APP_API_URL}/translations/${languageCode}`
    );
  },

  // Languages
  async getLanguages() {
    return axios.get(
      `${process.env.REACT_APP_API_URL}/languages/`
    );
  },

  // Activity Logs
  async getActivityLogs({
    userId,
    eventType,
    page = 1,
    limit = 20,
    sortBy = "createdAt",
    sortOrder = -1,
    search = "",
    entity = "",
  } = {}) {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/activity-logs`,
        {
          params: {
            userId,
            eventType,
            page,
            limit,
            sortBy,
            sortOrder,
            search,
            entity,
          },
        }
      );

      return res.data;
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          "Failed to fetch activity logs",
      };
    }
  },

  // Broadcasts
  async fetchBroadcasts() {
    return axios.get(
      `${process.env.REACT_APP_API_URL}/broadcast/`
    );
  },

  async createBroadcast(payload) {
    return axios.post(
      `${process.env.REACT_APP_API_URL}/broadcast/`, payload
    );
  },

  async getBroadcastById(id) {
    return axios.get(
      `${process.env.REACT_APP_API_URL}/broadcast/${id}`
    ).then(res => res.data);
  },

  async updateBroadcast(id, payload) {
    return axios.patch(
      `${process.env.REACT_APP_API_URL}/broadcast/${id}`, payload
    );
  },

  async deleteBroadcast(id) {
    return axios.delete(
      `${process.env.REACT_APP_API_URL}/broadcast/${id}`
    );
  },

  async sendBroadcast(id) {
    return axios.post(
      `${process.env.REACT_APP_API_URL}/broadcast/${id}/send`
    );
  },

  async fetchNotifications(params = {}) {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/notifications`,
      {
        params: {
          ...params,
          audience: "admin",
        },
      }
    );
    return res.data;
  },

  async updateNotification(id, payload) {
    const res = await axios.patch(
      `${process.env.REACT_APP_API_URL}/notifications/${id}`,
      payload
    );
    return res.data;
  },
  
  async markAllNotificationsAsRead() {
    const res = await axios.patch(
      `${process.env.REACT_APP_API_URL}/notifications/mark-all-read`
    );
    return res.data;
  },

  async resendEmailVerification(email) {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/user/resend-email-verification`,
        { email, source: "admin" }
      );
      return res.data;
    } catch (error) {
      return { error: error.response?.data?.error || "Failed to resend verification email" };
    }
  },

  async verifyEmailToken(token) {
    if (!token || typeof token !== 'string') throw new Error('Invalid token');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/user/verify-email`,
        { token }
      );
      return res.data;
    } catch (error) {
      return (
        error.response?.data || {
          error: error.response?.data?.error || error.response?.data?.message || 'Verification failed',
          message: error.response?.data?.message || error.response?.data?.error || 'Verification failed',
        }
      );
    }
  },
  
  async createRole (roleData) {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/role`, roleData );
      return response.data;
    } catch (error) {
      console.error("Create Role API error:", error.response || error.message);
      return {
        success: false,
        error: error.response?.data?.error || "Failed to create role",
      };
    }
  },

  async updateRolePermissions (roleId, permissions) {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/role/${roleId}/permissions`, { permissions },
      );
      return response.data;
    } catch (error) {
      console.error("Update Role Permissions API error:", error.response || error.message);
      return {
        success: false,
        error: error.response?.data?.error || "Failed to update permissions",
      };
    }
  },

  async getRoleById(roleId) {
    if (!roleId) {
      return { success: false, error: "Role ID is required" };
    }
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/role/${roleId}`);
      return response.data;
    } catch (error) {
      console.error("Get Role By ID API error:", error.response || error.message);
      return {
        success: false,
        error: error.response?.data?.error || "Failed to fetch role",
      };
    }
  },

  async deleteRole(roleId) {
    if (!roleId) {
      return { success: false, error: "Role ID is required" };
    }

    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/role/${roleId}`);
      return response.data;
    } catch (error) {
      console.error("Delete Role API error:", error.response || error.message);
      return {
        success: false,
        error: error.response?.data?.error || "Failed to delete role",
      };
    }
  },
  async fetchAllFlaggedContent(filters = {}) {
    try {
      const params = new URLSearchParams(filters).toString();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/flagged?${params}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching flagged content:", error);
      return { data: [], meta: {} };
    }
  },

  async updateFlaggedContentStatus(flagId, payload) {
    try {
      const response = await axios.patch(`${process.env.REACT_APP_API_URL}/flagged/${flagId}/status`, payload);
      return await response.data;
    } catch (error) {
      console.error("Error updating flagged content status:", error);
      return { error: "Failed to update flagged content status" };
    }
  },

  async createFlaggedContent(payload) {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/flagged`, payload);
      return await response.data;
    } catch (error) {
      console.error("Error creating flagged content:", error);
      return { error: "Failed to create flagged content" };
    }
  },

  async deleteFlaggedContent(id) {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/flagged/${id}`);
      return await response.data;
    } catch (error) {
      console.error("Error deleting flagged content:", error);
      return { error: "Failed to delete flagged content" };
    }
  },

  async uploadUserProfilePic(payload) {
    const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/profile-picture`, payload,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return res;
  },

  async deleteUserProfilePic(userId) {
    try {
      const res = await axios.delete(`${process.env.REACT_APP_API_URL}/user/${userId}`);
      return res.data;
    } catch (error) {
      console.error("Error deleting profile picture:", error);
      return { error: error.response?.data?.error || "Failed to delete profile picture" };
    }
  },

  async getSenderListingById(senderId) {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/sender-listing/packages/${senderId}`
      );
      return res.data;
    } catch (error) {
      return { error: error.response?.data?.error || "Request failed" };
    }
  },

  async getTravelerListingById(travelerId) {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/traveler-listing/baggages/${travelerId}`
      );
      return res.data;
    } catch (error) {
      return { error: error.response?.data?.error || "Request failed" };
    }
  },

  async fetchUserOrders (userId) {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/orders/user/${userId}`);
      return res.data;
    } catch (err) {
      console.error("Error fetching user orders:", err);
      return [];
    }
  },

  async exportAdminLogs(params) {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/activity-logs/admin-logs/export`,
        {
          params,
          responseType: "blob",
        }
      );

      return {
        success: true,
        data: response.data,
        headers: response.headers,
      };
    } catch (error) {
      return { success: false };
    }
  },

  async getBlockedIps(params = {}) {
    try {
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== "" && v !== "undefined")
      );
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/blocked-ips`, { params: cleanParams });
      return res.data;
    } catch (error) {
      return {
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Request failed",
      };
    }
  },
  
  async updateIPStatus(data) {
    try {
      const res = await axios.patch(`${process.env.REACT_APP_API_URL}/blocked-ips/update-status`, data);

      return res.data;
    } catch (error) {
      return {
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Request failed",
      };
    }
  },

  async unblockPermanentIp(ipAddress) {
    try {
      const res = await axios.delete(`${process.env.REACT_APP_API_URL}/blocked-ips/unblock-permanent`, {
        data: { ipAddress },
      });
      return res.data;
    } catch (error) {
      return {
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Request failed",
      };
    }
  },

  async getIpDetails(ipAddress) {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/blocked-ips/${encodeURIComponent(ipAddress)}/detail`
      );
      return res.data;
    } catch (error) {
      return {
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Request failed",
      };
    }
  },

  async getIpSecurityHistory(ipAddress, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/blocked-ips/${encodeURIComponent(ipAddress)}/history${queryString ? `?${queryString}` : ""}`
      );
      return res.data;
    } catch (error) {
      return {
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Request failed",
      };
    }
  },

  async createTemplate(data) {
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/notification-template`, data);
      return res.data;
    } catch (error) {
      return { error: error.response?.data?.error || error.response?.data?.message || "Request failed" };
    }
  },

  async getAllTemplates(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();

      const res = await axios.get(`${process.env.REACT_APP_API_URL}/notification-template${queryString ? `?${queryString}` : ""}`);
      return res.data;
    } catch (error) {
      return {
        error: error.response?.data?.error || error.response?.data?.message || "Request failed",
      };
    }
  },

  async getTemplateById(id) {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/notification-template/${id}`);
      return res.data;
    } catch (error) {
      return { error: error.response?.data?.error || "Request failed" };
    }
  },

  async updateTemplate(id, data) {
    try {
      const res = await axios.put(`${process.env.REACT_APP_API_URL}/notification-template/${id}`, data);
      return res.data;
    } catch (error) {
      return { error: error.response?.data?.error || error.response?.data?.message || "Request failed" };
    }
  },

  async deleteTemplate(id) {
    try {
      const res = await axios.delete(`${process.env.REACT_APP_API_URL}/notification-template/${id}`);
      return res.data;
    } catch (error) {
      return { error: error.response?.data?.error || error.response?.data?.message || "Request failed" };
    }
  },

  async previewEmailTemplate(templateId) {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/notification-template/${templateId}/preview`
      );

      return res.data;
    } catch (error) {
      return {
        error:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Request failed",
      };
    }
  },

  async getPlaceholders() {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/notification-template/placeholders`
      );

      return res.data;
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message || "Failed to fetch placeholders",
      };
    }
  },

  async previewTemplate(data) {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/notification-template/preview`,
        data
      );
      return res.data;
    } catch (error) {
      return { error: error.response?.data?.error || error.response?.data?.message || "Request failed" };
    }
  },

  async fetchPlaceSuggestions(term, type) {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/sender-listing/fetch-place-suggestions`,
      { params: { term, type } }
    );
    return res;
  },
  
  async sendPaymentReminder(orderId, role) {
    const res = await axios.post(`${process.env.REACT_APP_API_URL}/payments/remind`, { orderId, role });
    return res;
  },

  // Promo Codes
  async fetchPromoCodes() {
    return axios.get(`${process.env.REACT_APP_API_URL}/payments/promo-codes`);
  },

  async createPromoCode(payload) {
    return axios.post(`${process.env.REACT_APP_API_URL}/payments/promo-codes`, payload);
  },

  async updatePromoCode(id, payload) {
    return axios.patch(`${process.env.REACT_APP_API_URL}/payments/promo-codes/${id}`, payload);
  },

  async deletePromoCode(id) {
    return axios.delete(`${process.env.REACT_APP_API_URL}/payments/promo-codes/${id}`);
  },

  async getDashboard() {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/flagged/statistics`);
      return response.data;
    } catch (error) {
      console.error("Error fetching dashboard statistics:", error);
      return { success: false, error: error.response?.data?.error || "Failed to fetch stats" };
    }
  },

  async getVerification(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/flagged?queueType=verification&${queryParams}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching verification queue:", error);
      return { success: false, error: error.response?.data?.error || "Failed to fetch verification flags" };
    }
  },

  async getBehavior(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/flagged?queueType=behavioral_dispute&${queryParams}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching behavioral queue:", error);
      return { success: false, error: error.response?.data?.error || "Failed to fetch behavioral flags" };
    }
  },

  async getAppeals(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/flagged/appeals?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching appeals queue:", error);
      return { success: false, error: error.response?.data?.error || "Failed to fetch appeals" };
    }
  },

  async saveDecision(flagId, action, adminNotes) {
    try {
      const response = await axios.patch(`${process.env.REACT_APP_API_URL}/flagged/${flagId}/status`, { action, adminNotes });
      return response.data;
    } catch (error) {
      console.error("Error saving decision:", error);
      return { success: false, error: error.response?.data?.error || "Failed to save decision" };
    }
  },

  async saveGuidance(flagId, messageText) {
    try {
      const response = await axios.patch(`${process.env.REACT_APP_API_URL}/flagged/${flagId}/guidance`, { messageText });
      return response.data;
    } catch (error) {
      console.error("Error saving guidance message:", error);
      return { success: false, error: error.response?.data?.error || "Failed to save guidance message" };
    }
  },

  async getAppealDetails(appealId) {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/flagged/appeals/${appealId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching appeal details:", error);
      return { success: false, error: error.response?.data?.error || "Failed to fetch appeal details" };
    }
  },

  async reviewAppeal(appealId, decision, notes) {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/flagged/${appealId}/appeal-resolve`, { decision, notes });
      return response.data;
    } catch (error) {
      console.error("Error reviewing appeal:", error);
      return { success: false, error: error.response?.data?.error || "Failed to review appeal" };
    }
  },

  async getFlagDetails(flagId) {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/flagged/${flagId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching flag details:", error);
      return { success: false, error: error.response?.data?.error || "Failed to fetch flag details" };
    }
  },

  async getTimeline(flagId) {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/flagged/${flagId}/timeline`);
      return response.data;
    } catch (error) {
      console.error("Error fetching flag timeline:", error);
      return { success: false, error: error.response?.data?.error || "Failed to fetch timeline logs" };
    }
  },

  async changePriority(flagId, priority, notes) {
    try {
      const response = await axios.patch(`${process.env.REACT_APP_API_URL}/flagged/${flagId}/change-priority`, { priority, notes });
      return response.data;
    } catch (error) {
      console.error("Error changing flag priority:", error);
      return { success: false, error: error.response?.data?.error || "Failed to update priority" };
    }
  },

  async assignFlag(flagId, assignedTo, notes) {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/flagged/${flagId}/assign`, { assignedTo, notes });
      return response.data;
    } catch (error) {
      console.error("Error assigning flag:", error);
      return { success: false, error: error.response?.data?.error || "Failed to assign flag" };
    }
  },

  async exportFlagsCSV() {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/flagged/export`, { responseType: "blob" });
      return response.data;
    } catch (error) {
      console.error("Error exporting flags CSV:", error);
      throw error;
    }
  },

  async deleteFlag(flagId) {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/flagged/${flagId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting flag:", error);
      return { success: false, error: error.response?.data?.error || "Failed to delete flag" };
    }
  },

  async getFlaggedByUserId(userId) {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/flagged/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching flags by user:", error);
      return { success: false, data: [] };
    }
  },
};

export default userServices;